import { route, SandboxError, type Route } from '../adapter';
import { newId, now } from '../store';
import type { KitLine, SupplyItem, SupplyItemWrite } from '@/lib/supplies';
import type { ContactType } from '@/lib/contact-types';

/** Supplies, the program and its kit, contact types, system settings. */

function keyFrom(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'type';
}

export const catalogRoutes: readonly Route[] = [
  route('GET', '/supplies', ({ store }) => ({ data: store.supplies })),
  route('GET', '/supplies/:id', ({ store, params }) => {
    const item = store.supplies.find((row) => row._id === params['id']);
    if (!item) throw new SandboxError(404, 'Supply item not found.');
    return { data: item };
  }),
  route('POST', '/supplies', ({ store, body }) => {
    const item: SupplyItem = { ...(body as SupplyItemWrite), _id: newId(), updatedAt: now() };
    store.supplies = [...store.supplies, item];
    return { data: item };
  }),
  route('PATCH', '/supplies/:id', ({ store, params, body }) => {
    const index = store.supplies.findIndex((row) => row._id === params['id']);
    const existing = store.supplies[index];
    if (!existing) throw new SandboxError(404, 'Supply item not found.');
    const updated: SupplyItem = { ...existing, ...(body as Partial<SupplyItemWrite>), updatedAt: now() };
    store.supplies[index] = updated;
    return { data: updated };
  }),
  route('DELETE', '/supplies/:id', ({ store, params }) => {
    if ((store.program.kit ?? []).some((line) => line.itemId === params['id'])) throw new SandboxError(409, 'A program kit uses this item. Take it out of the kit first.');
    store.supplies = store.supplies.filter((row) => row._id !== params['id']);
    return { data: null };
  }),

  route('PUT', '/programs/:id/kit', ({ store, body }) => {
    const kit = ((body as { kit?: KitLine[] }).kit ?? []).filter((line) => store.supplies.some((item) => item._id === line.itemId));
    store.program = { ...store.program, kit };
    return { data: kit };
  }),

  route('GET', '/contact-types', ({ store }) => ({ data: [...store.contactTypes].sort((a, b) => a.order - b.order) })),
  route('POST', '/contact-types', ({ store, body }) => {
    const label = String((body as { label?: string }).label ?? '').trim();
    if (!label) throw new SandboxError(400, 'A label is required.');
    const type: ContactType = { _id: newId(), key: keyFrom(label), label, isActive: true, order: store.contactTypes.length };
    store.contactTypes = [...store.contactTypes, type];
    return { data: type };
  }),
  route('PATCH', '/contact-types/:id', ({ store, params, body }) => {
    const patch = body as Partial<Pick<ContactType, 'label' | 'isActive' | 'order'>>;
    const index = store.contactTypes.findIndex((row) => row._id === params['id']);
    const existing = store.contactTypes[index];
    if (!existing) throw new SandboxError(404, 'Contact type not found.');
    const updated: ContactType = { ...existing, ...patch };
    // Swap positions with whoever holds the wanted slot, as the arrows do.
    store.contactTypes = store.contactTypes.map((row) => (row._id === existing._id ? updated : patch.order !== undefined && row.order === patch.order ? { ...row, order: existing.order } : row));
    return { data: updated };
  }),

  route('GET', '/system-settings', ({ store }) => ({ data: store.settings })),
  route('PUT', '/system-settings', ({ store, body }) => {
    store.settings = { ...store.settings, ...((body as { settings?: object }).settings ?? {}) };
    return { data: store.settings };
  }),
];
